import { defineMessages } from '@atlassian/jira-intl';

const messages: {
	// Common
	linkDetailsSectionHeading: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	applinkStatusLoadingText: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	goToRemoteSiteLink: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	formCancelButtonText: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	formSaveButtonText: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	editMainTitle: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	seeAllApplinksButton: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	applinkNotFoundHeader: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	applinkNotFoundDescription: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	applinkNotEditableHeader: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	securedByMessage: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	productCardHeader: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	errorFlagTitle: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	// OAuth 1.0 Edit
	connectionsTitle: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	arrowDisabledHoverText: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	arrowMatchHoverText: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	arrowMismatchHoverText: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	authTableRowDirectionIncoming: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	authTableRowDirectionOutgoing: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	authTableColumnDirectionHeading: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	authTableColumnLocalAuthenticationHeading: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	authTableColumnRemoteAuthenticationHeading: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	impersonationRemovedTitle: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	impersonationRemovedDescription: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	unsupportedAuthenticationType: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	// OAuth 2.0 Edit
	outgoingCredentialsButtonText: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	outgoingCredentialsModalTitle: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	outgoingLinkTitle: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	outgoingLinkDescription: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	incomingLinkTitle: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	incomingLinkDescription: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	continueLinkingTitle: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	continueLinkingDescription: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	oauth2ConfigLoadFailedTitle: {
		defaultMessage: string;
		id: string;
		description: string;
	};
	oauth2ConfigLoadFailedDescription: {
		defaultMessage: string;
		id: string;
		description: string;
	};
} = defineMessages({
	// Common
	linkDetailsSectionHeading: {
		defaultMessage: 'Link details',
		id: 'applinks.administration.ui.edit.link-details-section-heading',
		description: 'Heading for the section containing application link details on the edit page.',
	},
	applinkStatusLoadingText: {
		defaultMessage: 'Loading application link status',
		id: 'applinks.administration.ui.edit.applink-status-loading-text',
		description:
			'Message that the application link status is currently being loaded, and will be displayed soon.',
	},
	goToRemoteSiteLink: {
		defaultMessage: 'Go to {displayUrl}',
		id: 'applinks.administration.ui.edit.go-to-remote-site-link',
		description: 'Clickable link which navigates the user to the site at URL {displayUrl}',
	},
	formCancelButtonText: {
		defaultMessage: 'Cancel',
		id: 'applinks.administration.ui.edit.form-cancel-button-text',
		description: 'Label for button which discards the changes made on the edit page.',
	},
	formSaveButtonText: {
		defaultMessage: 'Save',
		id: 'applinks.administration.ui.edit.form-save-button-text',
		description: 'Label for button which saves the changes made on the edit page.',
	},
	editMainTitle: {
		defaultMessage: 'Edit application link',
		id: 'applinks.administration.ui.edit.main.title',
		description: 'Title for the main edit application link section.',
	},
	seeAllApplinksButton: {
		defaultMessage: 'See all application links',
		id: 'applinks.administration.ui.edit.see-all-application-links-button',
		description:
			'Label for the button which navigates to the page displaying all application links.',
	},
	applinkNotFoundHeader: {
		defaultMessage: 'Application link not found',
		id: 'applinks.administration.ui.edit.applink-not-found-header',
		description: 'Header for the section displayed when an application link is not found.',
	},
	applinkNotFoundDescription: {
		defaultMessage:
			"The application link you're trying to edit does not exist or has been deleted.",
		id: 'applinks.administration.ui.edit.applink-not-found-description',
		description: 'Description for the section displayed when an application link is not found.',
	},
	applinkNotEditableHeader: {
		defaultMessage: 'Non-editable system link',
		id: 'applinks.administration.ui.edit.applink-not-editable-header',
		description: 'Header for the section displayed when an application link is not editable.',
	},
	securedByMessage: {
		defaultMessage: 'Secured by {authMethod}',
		id: 'applinks.administration.ui.edit.secured-by-message',
		description:
			'Message indicating the authentication method used for the application link. {authMethod} will be either "OAuth 1.0" or "OAuth 2.0".',
	},
	productCardHeader: {
		defaultMessage: 'Linked application',
		id: 'applinks.administration.ui.edit.product-card-header',
		description: 'Header for the product card displaying linked application details.',
	},
	errorFlagTitle: {
		defaultMessage: 'Failed to update application link',
		id: 'applinks.administration.ui.edit.error-flag-title',
		description: 'Title for the error flag displayed when an application link update fails.',
	},

	// OAuth 1.0 Edit
	connectionsTitle: {
		defaultMessage: 'Connections',
		id: 'applinks.administration.ui.edit.connections-section-header',
		description:
			'Heading for the section containing OAuth 1.0 connection details on the edit page.',
	},
	arrowDisabledHoverText: {
		defaultMessage: 'Authentication is disabled',
		id: 'applinks.administration.ui.edit.arrow-disabled-alt-text',
		description:
			'Accessible label for the authentication status arrow. Shown when the arrow is greyed out.',
	},
	arrowMatchHoverText: {
		defaultMessage: 'Authentication is enabled',
		id: 'applinks.administration.ui.edit.arrow-match-alt-text',
		description:
			'Accessible label for the authentication status arrow. Shown when the arrow is green (enabled).',
	},
	arrowMismatchHoverText: {
		defaultMessage: 'Authentication is mismatched between local and remote',
		id: 'applinks.administration.ui.edit.arrow-mismatch-alt-text',
		description:
			'Accessible label for the authentication status arrow. Shown when the arrow is red with a cross (error).',
	},
	authTableRowDirectionIncoming: {
		defaultMessage: 'Incoming',
		id: 'applinks.administration.ui.edit.direction-incoming',
		description: 'Table row heading for information about incoming authentication.',
	},
	authTableRowDirectionOutgoing: {
		defaultMessage: 'Outgoing',
		id: 'applinks.administration.ui.edit.direction-outgoing',
		description: 'Table row heading for information about outgoing authentication.',
	},
	authTableColumnDirectionHeading: {
		defaultMessage: 'Direction',
		id: 'applinks.administration.ui.edit.direction-heading',
		description: 'Table column heading for the direction of authentication.',
	},
	authTableColumnLocalAuthenticationHeading: {
		defaultMessage: 'Local authentication',
		id: 'applinks.administration.ui.edit.local-authentication-heading',
		description: 'Table column heading for local authentication.',
	},
	authTableColumnRemoteAuthenticationHeading: {
		defaultMessage: 'Remote authentication',
		id: 'applinks.administration.ui.edit.remote-authentication-heading',
		description: 'Table column heading for remote authentication.',
	},
	impersonationRemovedTitle: {
		defaultMessage: 'OAuth with impersonation is not supported',
		id: 'applinks.administration.ui.edit.impersonation-removed-title',
		description:
			'Title for the error message displayed when trying to use OAuth with impersonation.',
	},
	impersonationRemovedDescription: {
		defaultMessage:
			'Support for OAuth with impersonation <link>has been removed</link>. Please select a different authentication method.',
		id: 'applinks.administration.ui.edit.impersonation-removed-description',
		description: 'Error message displayed when trying to use OAuth with impersonation.',
	},
	unsupportedAuthenticationType: {
		defaultMessage:
			'Invalid authentication type "{invalidAuthType}" used. Please select a different authentication method.',
		id: 'applinks.administration.ui.edit.unsupported-authentication-type',
		description:
			'Error message displayed when an unsupported authentication type is used. {invalidAuthType} will be either "OAuth 2.0" or "OAuth with impersonation".',
	},

	// OAuth 2.0 Edit
	outgoingCredentialsButtonText: {
		defaultMessage: 'Update outgoing link credentials',
		id: 'applinks.administration.ui.edit.outgoing-credentials-button-text',
		description: 'Label for the button which opens the modal to update outgoing link credentials.',
	},
	outgoingCredentialsModalTitle: {
		defaultMessage: 'Update outgoing link credentials',
		id: 'applinks.administration.ui.edit.outgoing-credentials-modal-title',
		description: 'Title for the pop-up modal which lets the user update outgoing credentials.',
	},
	outgoingLinkTitle: {
		defaultMessage: 'Outgoing link',
		id: 'applinks.administration.ui.edit.outgoing-link.title',
		description: 'Title for the section displaying outgoing link details.',
	},
	outgoingLinkDescription: {
		defaultMessage:
			'Find these details in the external application and enter them below. You may need to ask an admin of the external application for these details.',
		id: 'applinks.administration.ui.edit.outgoing-link.description',
		description: 'Description for the section displaying incoming link details.',
	},
	incomingLinkTitle: {
		defaultMessage: 'Incoming link',
		id: 'applinks.administration.ui.edit.incoming-link.title',
		description: 'Title for the section displaying incoming link details.',
	},
	incomingLinkDescription: {
		defaultMessage:
			"Copy these details to the external application. If you're not an admin of the application, ask an existing admin to make the updates for you.",
		id: 'applinks.administration.ui.edit.incoming-link.description',
		description: 'Description for the section displaying incoming link details.',
	},
	continueLinkingTitle: {
		defaultMessage: 'Continue linking to the external application',
		id: 'applinks.administration.ui.edit.continue-linking.title',
		description:
			'Title for the section prompting the user to continue linking to the external application.',
	},
	continueLinkingDescription: {
		defaultMessage:
			'In order to complete the link, enter the credentials for the incoming and outgoing links below.',
		id: 'applinks.administration.ui.edit.continue-linking.description',
		description:
			'Description for the section prompting the user to continue linking to the external application.',
	},
	oauth2ConfigLoadFailedTitle: {
		defaultMessage: 'Failed to load the OAuth 2.0 configuration',
		id: 'applinks.administration.ui.edit.oauth2-config-load-failed-title',
		description:
			'Title for the error message displayed when OAuth 2.0 configuration fails to load.',
	},
	oauth2ConfigLoadFailedDescription: {
		defaultMessage:
			"We couldn't load the OAuth 2.0 configuration for this app. This may be because the target app is inaccessible.",
		id: 'applinks.administration.ui.edit.oauth2-config-load-failed-description',
		description:
			'Description for the error message displayed when OAuth 2.0 configuration fails to load.',
	},
});
export default messages;
